import React, { useState, useEffect } from 'react';
import { Zap, Search, X, Plus, RefreshCw } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../services/apiService';
import { createLogger } from '../../utils/logger';

const logger = createLogger('ActionPicker');

const ActionPicker = ({ 
  appName,
  onSelect, 
  onClose, 
  selectedAction, 
  multiple = false 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedActions, setSelectedActions] = useState(selectedAction ? (Array.isArray(selectedAction) ? selectedAction : [selectedAction]) : []);

  useEffect(() => {
    console.log('🔍 [ActionPicker] Component mounted/updated', { appName });
    fetchActions();
  }, [appName]);

  const fetchActions = async () => {
    if (!appName) {
      logger.warn('fetchActions', 'No appName provided');
      setLoading(false);
      setError('No app selected');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      logger.info('fetchActions', 'Fetching actions for app:', appName);
      
      // Use backend API instead of direct Firestore connection
      const actionsData = await apiService.getActions(appName);
      
      logger.info('fetchActions', 'Actions fetched', {
        count: actionsData?.length,
        sample: actionsData?.[0]
      });
      
      // Map data to ensure id field exists
      const mappedActions = actionsData.map(action => ({
        id: action.actionId || action.id,
        ...action
      }));
      
      console.log('✅ [ActionPicker] Actions loaded:', {
        count: mappedActions.length,
        actions: mappedActions,
        appName
      });
      
      setActions(mappedActions);
    } catch (error) {
      logger.error('fetchActions', 'Error fetching actions', error);
      console.error('❌ [ActionPicker] Error fetching actions:', error);
      setError(error.message || 'Failed to load actions');
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredActions = actions.filter(action =>
    action.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    action.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    action.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateNew = () => {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    navigate(`/dashboard/${appName}/actions?returnUrl=${returnUrl}`);
  };

  const handleSelectAction = (action) => {
    if (multiple) {
      const isSelected = selectedActions.find(a => a.id === action.id);
      if (isSelected) {
        setSelectedActions(selectedActions.filter(a => a.id !== action.id));
      } else {
        setSelectedActions([...selectedActions, action]);
      }
    } else {
      setSelectedActions([action]);
    }
  };

  const handleConfirm = () => {
    if (multiple) {
      onSelect(selectedActions);
    } else {
      onSelect(selectedActions[0]);
    }
    onClose();
  };

  const getActionTypeColor = (type) => {
    const colors = {
      navigate: 'bg-blue-100 text-blue-700',
      api: 'bg-green-100 text-green-700',
      validation: 'bg-orange-100 text-orange-700',
      dataManipulation: 'bg-purple-100 text-purple-700',
      notification: 'bg-yellow-100 text-yellow-700',
      custom: 'bg-gray-100 text-gray-700'
    };
    
    return colors[type] || colors.custom;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Zap size={24} className="text-yellow-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Select Action{multiple ? 's' : ''}
              </h2>
              <p className="text-sm text-gray-600">
                {multiple ? 'Choose one or more actions' : 'Choose an action for this step'} • {appName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchActions}
              className="text-gray-400 hover:text-yellow-600 transition p-2 hover:bg-yellow-50 rounded-lg"
              title="Refresh actions"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search actions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none"
              autoFocus
            />
          </div>
          {multiple && selectedActions.length > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedActions.length} action(s) selected
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
              <p>Loading actions for {appName}...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">
                <X size={48} className="mx-auto mb-2" />
                <p className="text-lg font-medium">Error loading actions</p>
                <p className="text-sm text-gray-600 mt-2">{error}</p>
              </div>
              <button
                onClick={fetchActions}
                className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
              >
                Retry
              </button>
            </div>
          ) : filteredActions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Zap size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No actions found</p>
              <p className="text-sm mt-2">
                {searchTerm ? `No actions match "${searchTerm}"` : `Create actions in the action builder first`}
              </p>
              {!searchTerm && (
                <p className="text-xs text-gray-500 mt-2">
                  App: {appName} • Total actions: {actions.length}
                </p>
              )}
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredActions.map(action => {
                const isSelected = selectedActions.find(a => a.id === action.id);
                return (
                  <div
                    key={action.id}
                    onClick={() => handleSelectAction(action)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                      isSelected
                        ? 'border-yellow-500 bg-yellow-50'
                        : 'border-gray-200 hover:border-yellow-300 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-gray-900">{action.name}</h4>
                          {action.type && (
                            <span className={`text-xs px-2 py-0.5 rounded ${getActionTypeColor(action.type)}`}>
                              {action.type}
                            </span>
                          )}
                          {isSelected && multiple && (
                            <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">
                              ✓ Selected
                            </span>
                          )}
                        </div>
                        {action.description && (
                          <p className="text-sm text-gray-600 mb-2">{action.description}</p>
                        )}
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          {action.actionType && (
                            <span>Type: {action.actionType}</span>
                          )}
                          {action.config && (
                            <>
                              <span>•</span>
                              <span>Configured</span>
                            </>
                          )}
                        </div>
                      </div>
                      {multiple && (
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          onChange={() => handleSelectAction(action)}
                          className="w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 text-yellow-600 hover:text-yellow-700 transition font-medium"
          >
            <Plus size={18} />
            Create New Action
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            {selectedActions.length > 0 && (
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
              >
                Select {selectedActions.length > 1 ? `${selectedActions.length} Actions` : 'Action'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionPicker;



